// Load a .vrm (VRM 0.x or 1.0) and make it fit the GathrXR avatar pipeline.
//
// @pixiv/three-vrm is imported lazily (it is only fetched the first time a VRM
// is shown or uploaded). The file bytes are cached per URL, but every avatar
// instance parses its own copy: a VRM instance (spring bones, expressions,
// first-person meshes) is bound to one scene graph and can't be cloned.
//
// Pipeline fit: our clips are rotation-only quaternion tracks retargeted by
// humanoid body part (src/avatar/libraryRetarget.ts → rig.ts humanoidKey). So
// the VRM's raw humanoid bones are renamed to Mixamo-style names (Hips, Spine,
// LeftArm, LeftForeArm, …) that humanoidKey and FixedRetargetedAvatar's jump /
// pose code already recognise. Skinning binds by object, three-vrm's humanoid,
// spring bones and look-at hold object references, so renaming is safe. The
// mixer then animates the raw bones directly; three-vrm's normalized-bone
// transfer is switched off so it can't overwrite them.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { VRM } from '@pixiv/three-vrm';
import { VRM_DESKTOP_TEXTURE_CAP, VRM_QUEST_TEXTURE_CAP, VRM_WARN_TRIANGLES } from './vrmFormat';

type VrmModule = typeof import('@pixiv/three-vrm');
let vrmModule: Promise<VrmModule> | null = null;
/** The three-vrm library, fetched on first use. */
export function loadVrmModule(): Promise<VrmModule> {
  if (!vrmModule) {
    vrmModule = import('@pixiv/three-vrm');
    vrmModule.catch(() => { vrmModule = null; });
  }
  return vrmModule;
}

export const isQuestDevice = () =>
  typeof navigator !== 'undefined' && /OculusBrowser|Quest/i.test(navigator.userAgent);

// VRM humanoid bone → the name our rig adapter recognises (Mixamo style).
const BONE_RENAME: Record<string, string> = {
  hips: 'Hips', spine: 'Spine', chest: 'Spine1', upperChest: 'Spine2', neck: 'Neck', head: 'Head',
  leftEye: 'LeftEye', rightEye: 'RightEye', jaw: 'Jaw',
};
for (const [side, S] of [['left', 'Left'], ['right', 'Right']] as const) {
  Object.assign(BONE_RENAME, {
    [`${side}Shoulder`]: `${S}Shoulder`, [`${side}UpperArm`]: `${S}Arm`, [`${side}LowerArm`]: `${S}ForeArm`, [`${side}Hand`]: `${S}Hand`,
    [`${side}UpperLeg`]: `${S}UpLeg`, [`${side}LowerLeg`]: `${S}Leg`, [`${side}Foot`]: `${S}Foot`, [`${side}Toes`]: `${S}ToeBase`,
    // three-vrm reports VRM 0.x thumbs with VRM 1.0 names (Metacarpal/Proximal/Distal).
    [`${side}ThumbMetacarpal`]: `${S}HandThumb1`, [`${side}ThumbProximal`]: `${S}HandThumb2`, [`${side}ThumbDistal`]: `${S}HandThumb3`,
  });
  for (const [f, F] of [['Index', 'Index'], ['Middle', 'Middle'], ['Ring', 'Ring'], ['Little', 'Pinky']]) {
    Object.assign(BONE_RENAME, {
      [`${side}${f}Proximal`]: `${S}Hand${F}1`, [`${side}${f}Intermediate`]: `${S}Hand${F}2`, [`${side}${f}Distal`]: `${S}Hand${F}3`,
    });
  }
}

/** VRM 1.0 required humanoid bones (VRM 0.x requires the same set). */
export const REQUIRED_VRM_BONES = [
  'hips', 'spine', 'head',
  'leftUpperArm', 'leftLowerArm', 'leftHand', 'rightUpperArm', 'rightLowerArm', 'rightHand',
  'leftUpperLeg', 'leftLowerLeg', 'leftFoot', 'rightUpperLeg', 'rightLowerLeg', 'rightFoot',
] as const;

export interface VrmStats {
  triangles: number;
  meshes: number;
  materials: number;
  textures: number;
  maxTextureSize: number;
  springJoints: number;
  /** Standing height in metres (bind pose bounds, feet to top). */
  height: number;
  heavy: boolean;
}

export interface VrmInstance {
  vrm: VRM;
  scene: THREE.Group;
  animations: THREE.AnimationClip[];
  stats: VrmStats;
}

const bufferCache = new Map<string, Promise<ArrayBuffer>>();
function fetchVrmBytes(url: string): Promise<ArrayBuffer> {
  let p = bufferCache.get(url);
  if (!p) {
    p = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`VRM download failed (${r.status})`);
      return r.arrayBuffer();
    });
    p.catch(() => bufferCache.delete(url));
    bufferCache.set(url, p);
  }
  return p;
}

/** Parse VRM bytes (a fresh, independent instance each call). Throws if it isn't a VRM. */
export async function parseVrm(bytes: ArrayBuffer): Promise<{ vrm: VRM; gltf: any }> {
  const { VRMLoaderPlugin } = await loadVrmModule();
  const loader = new GLTFLoader();
  loader.register((parser) => new VRMLoaderPlugin(parser, { autoUpdateHumanBones: false }));
  // parse() takes ownership of nothing, but slice so a cached buffer is never detached.
  const gltf = await loader.parseAsync(bytes.slice(0), '');
  const vrm = gltf.userData?.vrm as VRM | undefined;
  if (!vrm) throw new Error('This file is a glTF model but not a VRM avatar (no VRM data inside).');
  return { vrm, gltf };
}

export function measureVrm(vrm: VRM): VrmStats {
  let triangles = 0, meshes = 0;
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  vrm.scene.traverse((o: any) => {
    if (!o.isMesh || !o.geometry) return;
    meshes++;
    const g = o.geometry as THREE.BufferGeometry;
    triangles += Math.round((g.index ? g.index.count : g.attributes.position?.count || 0) / 3);
    for (const m of (Array.isArray(o.material) ? o.material : [o.material]) as any[]) {
      if (!m) continue;
      materials.add(m);
      for (const k of Object.keys(m)) { const v = m[k]; if (v?.isTexture) textures.add(v); }
      for (const u of Object.values(m.uniforms || {}) as any[]) if (u?.value?.isTexture) textures.add(u.value);
    }
  });
  let maxTextureSize = 0;
  for (const t of textures) {
    const img: any = t.image;
    if (img) maxTextureSize = Math.max(maxTextureSize, img.width || 0, img.height || 0);
  }
  vrm.scene.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(vrm.scene);
  const height = Number.isFinite(box.max.y - box.min.y) ? box.max.y - box.min.y : 0;
  const springJoints = (vrm.springBoneManager as any)?.joints?.size ?? 0;
  return { triangles, meshes, materials: materials.size, textures: textures.size, maxTextureSize, springJoints, height, heavy: triangles > VRM_WARN_TRIANGLES };
}

/** Downscale any texture bigger than `cap` (GPU memory on Quest). Returns how many were resized. */
export function capVrmTextures(root: THREE.Object3D, cap: number): number {
  const seen = new Set<THREE.Texture>();
  let resized = 0;
  const fit = (t: THREE.Texture) => {
    if (seen.has(t)) return;
    seen.add(t);
    const img: any = t.image;
    const w = img?.width || 0, h = img?.height || 0;
    if (!w || !h || Math.max(w, h) <= cap || typeof document === 'undefined') return;
    const s = cap / Math.max(w, h);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * s));
    canvas.height = Math.max(1, Math.round(h * s));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    try { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); } catch { return; }
    try { img.close?.(); } catch { /* not a bitmap */ }
    t.image = canvas;
    t.needsUpdate = true;
    resized++;
  };
  root.traverse((o: any) => {
    for (const m of (Array.isArray(o.material) ? o.material : o.material ? [o.material] : []) as any[]) {
      for (const k of Object.keys(m)) { const v = m[k]; if (v?.isTexture) fit(v); }
      for (const u of Object.values(m.uniforms || {}) as any[]) if (u?.value?.isTexture) fit(u.value);
    }
  });
  return resized;
}

/**
 * Turn a parsed VRM into a GathrXR avatar: +Z facing, raw bones renamed for
 * the retargeter, unused vertices/joints dropped, textures capped, outlines
 * off on Quest. Mutates and returns the VRM's scene.
 */
export async function prepareVrmForAvatar(vrm: VRM, gltf: any): Promise<VrmInstance> {
  const { VRMUtils } = await loadVrmModule();
  // VRM 0.x faces -Z; everything else in GathrXR faces +Z.
  if ((vrm.meta as any)?.metaVersion === '0') VRMUtils.rotateVRM0(vrm);
  VRMUtils.removeUnnecessaryVertices(vrm.scene);
  VRMUtils.combineSkeletons(vrm.scene);

  // Our mixer drives the raw bones; never copy the (idle) normalized rig over them.
  vrm.humanoid.autoUpdateHumanBones = false;

  // Rename raw humanoid bones. Any other node already using a target name
  // steps aside, because the mixer binds tracks by the first node with that name.
  const renames = new Map<THREE.Object3D, string>();
  for (const [bone, target] of Object.entries(BONE_RENAME)) {
    const node = vrm.humanoid.getRawBoneNode(bone as any);
    if (node) renames.set(node, target);
  }
  const taken = new Set(renames.values());
  vrm.scene.traverse((o) => { if (!renames.has(o) && taken.has(o.name)) o.name = `${o.name}_vrm`; });
  for (const [node, target] of renames) node.name = target;

  // The retargeter (libraryRetarget.captureRest) looks rest poses up by node
  // name and treats an unnamed parent as identity. A VRM 0.x scene root is
  // unnamed AND turned 180° (rotateVRM0), which flipped the body backwards.
  let n = 0;
  vrm.scene.traverse((o) => { if (!o.name) o.name = `__vrm_node_${n++}`; });

  const quest = isQuestDevice();
  capVrmTextures(vrm.scene, quest ? VRM_QUEST_TEXTURE_CAP : VRM_DESKTOP_TEXTURE_CAP);
  vrm.scene.traverse((o: any) => {
    // Bind-pose bounds don't follow the animated body; never cull the avatar.
    if (o.isSkinnedMesh) o.frustumCulled = false;
    o.castShadow = true;
    if (quest) {
      // MToon outlines draw every mesh a second time — skip them on Quest.
      for (const m of (Array.isArray(o.material) ? o.material : o.material ? [o.material] : []) as any[]) {
        if (m?.isMToonMaterial && 'outlineWidthMode' in m) m.outlineWidthMode = 'none';
      }
    }
  });

  vrm.scene.updateMatrixWorld(true);
  const stats = measureVrm(vrm);
  return { vrm, scene: vrm.scene as THREE.Group, animations: (gltf.animations as THREE.AnimationClip[]) || [], stats };
}

/** Download (cached bytes) and build a fresh, ready-to-animate VRM avatar instance. */
export async function loadVrmAvatar(url: string): Promise<VrmInstance> {
  const bytes = await fetchVrmBytes(url);
  const { vrm, gltf } = await parseVrm(bytes);
  return prepareVrmForAvatar(vrm, gltf);
}

/** Free a VRM instance's GPU resources. */
export async function disposeVrm(inst: VrmInstance) {
  try {
    const { VRMUtils } = await loadVrmModule();
    inst.scene.removeFromParent();
    VRMUtils.deepDispose(inst.scene);
  } catch { /* already gone */ }
}

/**
 * Warm a VRM before it's shown (in-world avatar switch): download the bytes
 * and the three-vrm library, and check the file header. Each avatar instance
 * still parses its own copy when it mounts. Rejects if the file won't load.
 */
export async function preloadVrm(url: string): Promise<void> {
  const [bytes] = await Promise.all([fetchVrmBytes(url), loadVrmModule()]);
  const magic = new TextDecoder().decode(new Uint8Array(bytes, 0, Math.min(4, bytes.byteLength)));
  if (magic !== 'glTF') { bufferCache.delete(url); throw new Error('Not a VRM / glTF binary'); }
}
